import React, { useState, useEffect } from 'react';
import { WORKER_URL } from '../../utils/api';
import Login from '../Login';

// ─── Constantes de preço ───────────────────────────────────────────────────
const PRICE_FULL = 49.90;
const PRICE_EARLY = 34.90;
const EARLY_TOTAL = 100;
const EARLY_BASE = 50; // vagas preenchidas antes do primeiro cadastro real

// ─── Variáveis CSS globais ─────────────────────────────────────────────────
const CSS_VARS = `
  @import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;600;700&display=swap');

  :root {
    --navy: #1B2A7A;
    --navy-ink: #0F1A4E;
    --navy-deep: #080F2F;
    --yellow: #FFD400;
    --yellow-warm: #F0A020;
    --red: #E63946;
    --blue: #2E7DE0;
    --green: #12A150;
    --orange: #F08A24;
    --bg: #FAF8F3;
    --bg-soft: #F2EFE8;
    --ink: #0F1A2E;
    --ink-2: #4A5072;
    --ink-3: #8A90A8;
    --line: rgba(10,15,50,0.10);
    --line-2: rgba(10,15,50,0.18);
  }

  * { margin: 0; padding: 0; box-sizing: border-box; }
  html { scroll-behavior: smooth; }
  body { background: var(--bg); color: var(--ink); font-family: 'Inter', system-ui, sans-serif; -webkit-font-smoothing: antialiased; }

  .lp-container { max-width: 1160px; margin: 0 auto; padding: 0 40px; }
  .lp-container-narrow { max-width: 860px; margin: 0 auto; padding: 0 40px; }
  .lp-section { padding: 96px 0; }
  .lp-section-sm { padding: 56px 0; }

  .lp-eyebrow { font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: var(--ink-3); margin-bottom: 20px; }
  .lp-display { font-family: 'Archivo', sans-serif; }
  .lp-mono { font-family: 'JetBrains Mono', monospace; }
  .lp-badge { display: inline-flex; align-items: center; gap: 8px; background: rgba(27,42,122,0.08); border: 1px solid rgba(27,42,122,0.18); border-radius: 999px; padding: 5px 14px; font-size: 12px; font-weight: 600; color: var(--navy); }
  .lp-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--green); box-shadow: 0 0 0 3px rgba(18,161,80,0.2); }

  .lp-btn-primary {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--navy); color: white;
    padding: 14px 24px; border-radius: 10px;
    font-family: 'Archivo', sans-serif; font-size: 15px; font-weight: 700;
    cursor: pointer; border: none; text-decoration: none;
    transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  }
  .lp-btn-primary:hover { background: #0F1A4E; transform: translateY(-1px); box-shadow: 0 12px 28px -12px rgba(27,42,122,0.5); }

  .lp-btn-ghost {
    display: inline-flex; align-items: center; gap: 8px;
    background: transparent; color: var(--ink-2);
    padding: 14px 24px; border-radius: 10px;
    font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 500;
    cursor: pointer; border: 1px solid var(--line-2); text-decoration: none;
    transition: all 0.15s ease;
  }
  .lp-btn-ghost:hover { border-color: var(--navy); color: var(--navy); }

  .lp-btn-yellow {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--yellow); color: var(--navy-ink);
    padding: 14px 24px; border-radius: 10px;
    font-family: 'Archivo', sans-serif; font-size: 15px; font-weight: 800;
    cursor: pointer; border: none; text-decoration: none;
    transition: all 0.15s ease;
  }
  .lp-btn-yellow:hover { background: #E6BE00; transform: translateY(-1px); box-shadow: 0 12px 28px -12px rgba(255,212,0,0.6); }

  .lp-microcopy { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; font-size: 12px; color: var(--ink-3); margin-top: 16px; }
  .lp-microcopy span::before { content: '✓ '; color: var(--green); font-weight: 700; }

  .lp-card {
    background: white; border-radius: 14px; padding: 24px;
    border: 1px solid var(--line);
    display: flex; flex-direction: column;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .lp-card:hover { transform: translateY(-2px); box-shadow: 0 16px 32px -16px rgba(10,15,50,0.15); }
  .lp-card-body { flex: 1; }

  .lp-reveal { opacity: 0; transform: translateY(24px); transition: opacity 0.6s ease, transform 0.6s ease; }
  .lp-reveal.lp-in { opacity: 1; transform: none; }

  @keyframes lp-pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
  @keyframes lp-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }

  @media (max-width: 768px) {
    .lp-container, .lp-container-narrow { padding: 0 20px; }
    .lp-section { padding: 64px 0; }
    .lp-nav-links { display: none !important; }
    .lp-hero-grid { flex-direction: column !important; }
    .lp-grid-2 { grid-template-columns: 1fr !important; }
    .lp-grid-3 { grid-template-columns: 1fr !important; }
    .lp-screen-sidebar { display: none !important; }
  }

  /* Accordion FAQ */
  .lp-faq-answer { overflow: hidden; transition: max-height 0.3s ease; }

  /* Scarcity progress bar */
  @keyframes lp-progress-glow { 0%,100%{box-shadow:0 0 16px rgba(255,212,0,0.4)} 50%{box-shadow:0 0 28px rgba(255,212,0,0.7)} }
`;

// ─── Hook: scroll reveal ───────────────────────────────────────────────────
function useReveal() {
  const ref = React.useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { el.classList.add('lp-in'); io.disconnect(); } }),
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, delay = 0, style }) {
  const ref = useReveal();
  return (
    <div ref={ref} className="lp-reveal" style={{ transitionDelay: `${delay}ms`, ...style }}>
      {children}
    </div>
  );
}

// ─── Ícones SVG ───────────────────────────────────────────────────────────
const Icon = {
  ArrowRight: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  Bell: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>,
  Clock: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Chart: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
  Box: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
  Doc: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  Shield: ({ width = 20, height = 20, strokeWidth = 2 }) => <svg width={width} height={height} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  Device: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  Moon: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
  Check: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  Plus: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  Volume: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>,
  Spark: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.39 6.95L21 12l-6.61 3.05L12 22l-2.39-6.95L3 12l6.61-3.05L12 2z"/></svg>,
  MessageCircle: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
};

// ─── Logo ──────────────────────────────────────────────────────────────────
function IntegraLogo({ size = 'md' }) {
  const fz = { sm: 20, md: 26, lg: 34 }[size];
  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 0.95 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.15em', fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: fz, letterSpacing: '-0.035em' }}>
        <span style={{ color: 'var(--navy)' }}>Integra</span>
        <span style={{ display: 'inline-flex' }}>
          <span style={{ color: 'var(--yellow-warm)' }}>M</span>
          <span style={{ color: 'var(--red)' }}>K</span>
          <span style={{ color: 'var(--blue)' }}>P</span>
        </span>
      </div>
      <div style={{ width: '100%', height: 3, background: 'linear-gradient(90deg, var(--yellow) 0%, var(--red) 50%, var(--blue) 100%)', marginTop: 3, borderRadius: 2 }} />
    </div>
  );
}

// ─── Tela Dashboard (mock visual) ─────────────────────────────────────────
function DashboardScreen() {
  const points = [0, 60, 120, 180, 260, 340, 310, 420, 390, 520, 640, 710, 680, 780, 830];
  const max = 900; const w = 400; const h = 80;
  const step = w / (points.length - 1);
  const path = points.map((v, i) => `${i === 0 ? 'M' : 'L'} ${i * step} ${h - (v / max) * h}`).join(' ');
  const areaPath = `${path} L ${w} ${h} L 0 ${h} Z`;

  return (
    <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 40px 80px -30px rgba(10,15,50,0.35), 0 8px 20px -8px rgba(10,15,50,0.1)', border: '1px solid rgba(10,15,50,0.08)', display: 'flex', fontFamily: "'Inter', sans-serif", minHeight: 480 }}>
      {/* Sidebar */}
      <div className="lp-screen-sidebar" style={{ width: 160, borderRight: '1px solid #ECECEC', padding: '16px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ padding: '2px 4px 14px' }}><IntegraLogo size="sm" /></div>
        {['Dashboard','Produtos','Vendas','Documentos','Configurações'].map((it, i) => (
          <div key={it} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 9px', borderRadius: 7, fontSize: 12, fontWeight: 500, color: i === 0 ? 'var(--navy)' : '#5A5F7A', background: i === 0 ? 'rgba(27,42,122,0.07)' : 'transparent' }}>
            {it}
          </div>
        ))}
      </div>
      {/* Content */}
      <div style={{ flex: 1, padding: '18px 20px', background: '#FAFAFC' }}>
        <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: 18, fontWeight: 700, color: 'var(--navy)', marginBottom: 14, letterSpacing: '-0.015em' }}>Dashboard</div>
        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Vendas hoje', value: 'R$ 1.284', delta: '+18%', color: 'var(--green)' },
            { label: 'Pedidos', value: '17', delta: 'novos', color: 'var(--navy)', pulse: true },
            { label: 'Perguntas', value: '3', delta: 'responder', color: 'var(--red)', pulse: true },
            { label: 'Mês atual', value: '142', delta: '+22%', color: 'var(--green)' },
          ].map((k, i) => (
            <div key={i} style={{ background: '#fff', borderRadius: 9, padding: '10px 12px', border: '1px solid #ECECEC' }}>
              <div style={{ fontSize: 9, fontWeight: 600, color: '#6A7091', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 5 }}>
                {k.label}
                {k.pulse && <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--red)', boxShadow: '0 0 0 2px rgba(230,57,70,0.2)' }} />}
              </div>
              <div style={{ fontFamily: "'Archivo', sans-serif", fontSize: 19, fontWeight: 800, color: 'var(--navy)', marginTop: 3, letterSpacing: '-0.02em' }}>{k.value}</div>
              <div style={{ fontSize: 10, color: k.color, fontWeight: 600, marginTop: 1 }}>{k.delta}</div>
            </div>
          ))}
        </div>
        {/* FLEX alert */}
        <div style={{ background: 'var(--yellow)', borderRadius: 9, padding: '10px 13px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #D9B800' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ background: 'var(--navy)', color: 'var(--yellow)', fontFamily: "'Archivo', sans-serif", fontWeight: 900, fontSize: 10, padding: '2px 7px', borderRadius: 4, letterSpacing: '0.06em' }}>FLEX</div>
            <div style={{ fontSize: 12, color: 'var(--navy-ink)', fontWeight: 600 }}>3 pedidos pendentes · Envio até 23:59</div>
          </div>
          <div style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", color: 'var(--navy-ink)', fontWeight: 600 }}>⏱ 4h 12min</div>
        </div>
        {/* Chart */}
        <div style={{ background: '#fff', borderRadius: 9, padding: '12px 14px', border: '1px solid #ECECEC' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Vendas · Hoje</div>
          <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 75 }}>
            <defs>
              <linearGradient id="dashGrad2" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="var(--navy)" stopOpacity="0.2" />
                <stop offset="100%" stopColor="var(--navy)" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={areaPath} fill="url(#dashGrad2)" />
            <path d={path} fill="none" stroke="var(--navy)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            <circle cx={w - step} cy={h - (points[points.length - 1] / max) * h} r="4" fill="var(--yellow)" stroke="var(--navy)" strokeWidth="2" />
          </svg>
        </div>
      </div>
    </div>
  );
}

// ─── Nav ───────────────────────────────────────────────────────────────────
function Nav({ onLogin, onRegister }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: scrolled ? 'rgba(250,248,243,0.92)' : 'transparent',
      backdropFilter: scrolled ? 'saturate(160%) blur(14px)' : 'none',
      WebkitBackdropFilter: scrolled ? 'saturate(160%) blur(14px)' : 'none',
      borderBottom: scrolled ? '1px solid var(--line)' : 'none',
      transition: 'all 0.3s',
    }}>
      <div className="lp-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 40px' }}>
        <IntegraLogo size="md" />
        <div className="lp-nav-links" style={{ display: 'flex', gap: 28, fontSize: 14, fontWeight: 500, color: 'var(--ink-2)' }}>
          {[['#produto','Produto'],['#preco','Preço'],['#roadmap','Roadmap'],['#faq','FAQ'],['/blog','Blog']].map(([href, label]) => (
            <a key={href} href={href} style={{ textDecoration: 'none', color: 'var(--ink-2)', transition: 'color 0.15s' }}
              onMouseEnter={e => e.target.style.color = 'var(--navy)'}
              onMouseLeave={e => e.target.style.color = 'var(--ink-2)'}>{label}</a>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="lp-btn-ghost" style={{ padding: '9px 18px', fontSize: 13 }} onClick={onLogin}>Entrar</button>
          <button className="lp-btn-yellow" style={{ padding: '9px 18px', fontSize: 13 }} onClick={onRegister}>
            Começar 7 dias grátis <Icon.ArrowRight />
          </button>
        </div>
      </div>
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────
function Hero({ onRegister }) {
  const [idx, setIdx] = useState(0);
  const headlines = [
    <>Você vende no Mercado&nbsp;Livre todo&nbsp;dia.<br /><span style={{ color: 'var(--ink-3)' }}>Mas ainda descobre seus pedidos</span> <em style={{ fontStyle: 'normal', color: 'var(--red)' }}>atrasados por acaso.</em></>,
    <>Controle total da sua loja no <span style={{ color: 'var(--navy)' }}>Mercado&nbsp;Livre</span>.<br /><span style={{ color: 'var(--ink-3)' }}>Em tempo real.</span> <span style={{ background: 'var(--yellow)', padding: '0 8px', borderRadius: 5 }}>Por menos de R$2/dia.</span></>,
    <>O painel do ML foi feito pra&nbsp;<em style={{ fontStyle: 'normal', color: 'var(--ink-3)' }}>eles.</em><br />O <span style={{ color: 'var(--navy)' }}>Integra&nbsp;MKP</span> foi feito pra&nbsp;<span style={{ color: 'var(--red)' }}>você.</span></>,
  ];

  return (
    <section style={{ paddingTop: 60, paddingBottom: 80, position: 'relative', overflow: 'hidden' }}>
      {/* Grid decorativo */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(rgba(27,42,122,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(27,42,122,0.05) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
        maskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, black 40%, transparent 80%)',
        WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 40%, black 40%, transparent 80%)',
      }} />
      <div className="lp-container" style={{ position: 'relative' }}>
        {/* Badge + seletor de headline */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
          <span className="lp-badge"><span className="lp-dot" />Disponível agora · Abril 2026</span>
          <div style={{ display: 'flex', gap: 4, marginLeft: 'auto', alignItems: 'center' }}>
            <span className="lp-mono" style={{ fontSize: 10, color: 'var(--ink-3)', marginRight: 6 }}>headline {idx + 1}/3</span>
            {headlines.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} style={{
                width: i === idx ? 22 : 7, height: 7, borderRadius: 4,
                background: i === idx ? 'var(--navy)' : 'var(--line-2)',
                border: 'none', cursor: 'pointer', transition: 'all 0.2s ease',
              }} />
            ))}
          </div>
        </div>

        <div style={{ maxWidth: 960 }}>
          <h1 className="lp-display" style={{ fontSize: 'clamp(48px, 6.5vw, 88px)', fontWeight: 800, lineHeight: 0.98, letterSpacing: '-0.035em', marginBottom: 24 }}>
            {headlines[idx]}
          </h1>
          <p style={{ fontSize: 'clamp(18px, 1.5vw, 20px)', color: 'var(--ink-2)', maxWidth: 640, marginBottom: 32, lineHeight: 1.55 }}>
            Dashboard em tempo real com alertas de pedidos, controle de prazo de envio e gestão completa de produtos — tudo em um só lugar, sem complicação.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="lp-btn-yellow" style={{ fontSize: 16, padding: '15px 28px' }} onClick={onRegister}>
              Começar 7 dias grátis <Icon.ArrowRight />
            </button>
            <a href="#produto" className="lp-btn-ghost" style={{ fontSize: 16, padding: '15px 28px' }}>Ver o produto</a>
          </div>
          <div className="lp-microcopy">
            <span>Sem cartão de crédito</span>
            <span>Acesso imediato</span>
            <span>Cancele quando quiser</span>
          </div>
        </div>

        {/* Dashboard visual flutuante */}
        <div style={{ marginTop: 72, position: 'relative', maxWidth: 1060, marginLeft: 'auto', marginRight: 'auto', animation: 'lp-float 6s ease-in-out infinite' }}>
          <DashboardScreen />
          {/* Notificação flutuante */}
          <div style={{ position: 'absolute', top: -22, right: 8, background: 'var(--navy-ink)', color: 'white', padding: '11px 14px', borderRadius: 10, fontSize: 12, fontWeight: 500, boxShadow: '0 18px 40px -12px rgba(10,15,50,0.4)', display: 'flex', gap: 10, alignItems: 'center', transform: 'rotate(2deg)' }}>
            <span style={{ width: 7, height: 7, background: 'var(--green)', borderRadius: '50%', boxShadow: '0 0 0 3px rgba(18,161,80,0.3)' }} />
            <div>
              <div className="lp-mono" style={{ fontSize: 9, color: 'var(--ink-3)', textTransform: 'uppercase' }}>NOVO PEDIDO</div>
              <div className="lp-display" style={{ fontWeight: 700, fontSize: 14 }}>R$ 117,00 · FLEX</div>
            </div>
          </div>
          {/* Alerta flutuante */}
          <div style={{ position: 'absolute', bottom: 28, left: -14, background: 'var(--yellow)', color: 'var(--navy-ink)', padding: '9px 13px', borderRadius: 10, fontSize: 12, fontWeight: 700, fontFamily: "'Archivo', sans-serif", boxShadow: '0 18px 40px -12px rgba(255,212,0,0.5)', transform: 'rotate(-3deg)', display: 'flex', gap: 9, alignItems: 'center' }}>
            <Icon.Clock />FLEX vence em 4h 12min
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Bloco de abertura ────────────────────────────────────────────────────
function Opening() {
  return (
    <section className="lp-section" id="produto">
      <div className="lp-container-narrow">
        <Reveal>
          <div className="lp-eyebrow">— Por que o Integra MKP</div>
          <h2 className="lp-display" style={{ fontSize: 'clamp(28px, 4vw, 52px)', fontWeight: 700, lineHeight: 1.05, marginBottom: 32, letterSpacing: '-0.03em' }}>
            Vender no Mercado&nbsp;Livre <em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--ink-3)' }}>deveria ser</em> simples.<br />
            Mas na prática você vive <span style={{ color: 'var(--red)' }}>apagando incêndio</span>.
          </h2>
          <div style={{ fontSize: 18, lineHeight: 1.65, color: 'var(--ink-2)', display: 'flex', flexDirection: 'column', gap: 18 }}>
            <p>Pedido que apareceu tarde, prazo de envio que quase venceu, pergunta de cliente que ficou sem resposta.</p>
            <p style={{ fontSize: 21, fontWeight: 600, color: 'var(--ink)', fontFamily: "'Archivo', sans-serif" }}>
              O painel do ML te dá informação. Mas não te dá <u style={{ textDecorationColor: 'var(--yellow)', textDecorationThickness: 4, textUnderlineOffset: 4 }}>controle</u>.
            </p>
            <p>O Integra MKP foi construído por quem vende — pensando em quem vende. É o painel que o Mercado Livre nunca fez: simples, rápido e com tudo que realmente importa na tela, atualizado em tempo real.</p>
            <p style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink)', borderLeft: '3px solid var(--yellow)', paddingLeft: 20 }}>
              Não é mais uma ferramenta pra aprender. É o controle que você já devia ter.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Dores ────────────────────────────────────────────────────────────────
function Pain() {
  const items = [
    'Você abre o painel do ML pela quinta vez no dia só pra ver se entrou pedido novo',
    'Um pedido FLEX quase atrasou porque você não viu a tempo — e sua reputação agradece não ter atrasado',
    'Você perdeu uma pergunta de cliente no meio de dezenas de notificações',
    'Na hora de fechar o mês, você não sabe exatamente quanto vendeu nem quais produtos puxaram mais',
    'Você fica no celular depois das 22h verificando a loja porque não tem como confiar que está tudo em ordem',
    'Você usa planilha, post-it ou memória pra controlar o que o sistema devia controlar por você',
  ];
  return (
    <section className="lp-section" style={{ background: 'var(--navy-ink)', color: 'var(--bg)' }}>
      <div className="lp-container">
        <Reveal>
          <div className="lp-eyebrow" style={{ color: 'var(--yellow)' }}>— Identificação</div>
          <h2 className="lp-display" style={{ fontSize: 'clamp(32px, 4.5vw, 64px)', fontWeight: 800, lineHeight: 0.98, letterSpacing: '-0.03em', marginBottom: 52, maxWidth: 860 }}>
            Você reconhece alguma<br />dessas situações?
          </h2>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.08)' }} className="lp-grid-2">
          {items.map((item, i) => (
            <Reveal key={i} delay={i * 60}>
              <div style={{ padding: '28px 24px', background: 'var(--navy-ink)', fontSize: 17, lineHeight: 1.45, display: 'flex', gap: 16, minHeight: 140, alignItems: 'flex-start' }}>
                <span className="lp-mono" style={{ fontSize: 11, color: 'var(--yellow)', fontWeight: 600, minWidth: 26, paddingTop: 3 }}>0{i + 1}</span>
                <span style={{ color: 'rgba(250,248,243,0.92)' }}>{item}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p style={{ marginTop: 48, fontSize: 20, fontFamily: "'Archivo', sans-serif", fontWeight: 600, maxWidth: 740, lineHeight: 1.4, letterSpacing: '-0.01em' }}>
            Se você se identificou com pelo menos uma dessas situações, o problema não é você — <span style={{ background: 'var(--yellow)', color: 'var(--navy-ink)', padding: '0 7px' }}>é a ferramenta que você está usando.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Solução ──────────────────────────────────────────────────────────────
function Solution() {
  return (
    <section className="lp-section">
      <div className="lp-container">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 72, alignItems: 'center' }} className="lp-grid-2">
          <Reveal>
            <div className="lp-eyebrow">— Como funciona</div>
            <h2 className="lp-display" style={{ fontSize: 'clamp(28px, 3.5vw, 50px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-0.03em', marginBottom: 24 }}>
              Conecta direto na API oficial. Transforma dados em <em style={{ fontStyle: 'italic', color: 'var(--navy)' }}>controle.</em>
            </h2>
            <div style={{ fontSize: 16, lineHeight: 1.6, color: 'var(--ink-2)', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p>O Integra MKP conecta direto na API oficial do Mercado Livre e transforma os dados da sua loja em informação útil, visual e em tempo real.</p>
              <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 13 }}>
                {[
                  { icon: <Icon.Bell />, bg: 'var(--yellow)', color: 'var(--navy-ink)', text: <><b style={{ color: 'var(--ink)' }}>Assim que entra um pedido, você sabe.</b> Sem recarregar, sem abrir aba nenhuma.</> },
                  { icon: <Icon.Clock />, bg: 'var(--red)', color: 'white', text: <><b style={{ color: 'var(--ink)' }}>Assim que um prazo aperta, o alerta aparece.</b> Com cor, com som, antes de virar multa.</> },
                  { icon: <Icon.Shield />, bg: 'var(--blue)', color: 'white', text: <><b style={{ color: 'var(--ink)' }}>Criptografia de ponta a ponta.</b> Zero acesso a senha ou dados bancários.</> },
                ].map((it, i) => (
                  <li key={i} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                    <span style={{ width: 28, height: 28, borderRadius: 8, background: it.bg, color: it.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>{it.icon}</span>
                    <div style={{ fontSize: 15 }}>{it.text}</div>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={150}>
            <div style={{ position: 'relative' }}>
              <DashboardScreen />
              <div style={{ position: 'absolute', top: -14, left: -14, background: 'white', padding: '9px 13px', borderRadius: 100, fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", color: 'var(--navy)', boxShadow: '0 12px 28px -10px rgba(10,15,50,0.2)', border: '1px solid var(--line-2)', display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)' }} />
                API oficial · criptografado
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ─── Benefícios ───────────────────────────────────────────────────────────
function Benefits() {
  const [soundPlaying, setSoundPlaying] = useState(false);
  const [questionSoundPlaying, setQuestionSoundPlaying] = useState(false);

  const playSound = async (file, setPlaying) => {
    if (setPlaying === setSoundPlaying && soundPlaying) return;
    if (setPlaying === setQuestionSoundPlaying && questionSoundPlaying) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const res = await fetch(`/sounds/${file}`);
      const arr = await res.arrayBuffer();
      const buf = await ctx.decodeAudioData(arr);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      setPlaying(true);
      src.start(0);
      src.onended = () => { setPlaying(false); ctx.close(); };
    } catch (e) { setPlaying(false); }
  };

  const playBeep = () => playSound('venda.mp3', setSoundPlaying);

  const benefits = [
    {
      icon: <Icon.Bell />, color: 'var(--red)', title: 'Nunca mais perca um pedido',
      body: 'Alertas sonoros avisam em tempo real quando um novo pedido entra, mesmo com outras abas abertas.',
      extra: (
        <button onClick={playBeep} style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px', background: 'var(--navy-ink)', color: 'var(--yellow)', borderRadius: 7, fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer', border: 'none', transition: 'transform 0.15s ease', transform: soundPlaying ? 'scale(0.96)' : 'scale(1)' }}>
          <Icon.Volume /> {soundPlaying ? 'Tocando…' : 'Ouvir o alerta'}
        </button>
      )
    },
    {
      icon: <Icon.MessageCircle />, color: 'var(--blue)', title: 'Responda perguntas na hora',
      body: 'Quando um cliente faz uma pergunta no seu anúncio, você recebe alerta sonoro e visual em tempo real — e pode responder direto pelo Integra MKP, sem abrir o ML.',
      extra: (
        <button onClick={() => playSound('pergunta.mp3', setQuestionSoundPlaying)} style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px', background: 'var(--navy-ink)', color: 'var(--yellow)', borderRadius: 7, fontSize: 11, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer', border: 'none', transition: 'transform 0.15s ease', transform: questionSoundPlaying ? 'scale(0.96)' : 'scale(1)' }}>
          <Icon.Volume /> {questionSoundPlaying ? 'Tocando…' : 'Ouvir o alerta'}
        </button>
      )
    },
    {
      icon: <Icon.Clock />, color: 'var(--yellow-warm)', title: 'Envie sempre no prazo',
      body: 'FLEX, AGÊNCIA, RETIRADA, FULL e mais — cada tipo com código de cor pra você saber exatamente o que é urgente.',
      extra: (
        <div style={{ display: 'flex', gap: 5, marginTop: 13, flexWrap: 'wrap' }}>
          {[['var(--green)', 'rgba(18,161,80,0.12)', 'FLEX'], ['var(--orange)', 'rgba(240,138,36,0.12)', 'AGÊNCIA'], ['var(--red)', 'rgba(230,57,70,0.12)', 'RETIRADA'], ['var(--blue)', 'rgba(46,125,224,0.12)', 'FULL'], ['var(--ink-3)', 'rgba(10,15,50,0.06)', '...']].map(([c, bg, label]) => (
            <span key={label} style={{ padding: '2px 9px', borderRadius: 100, background: bg, color: c, fontSize: 10, fontWeight: 700 }}>{label !== '...' ? '● ' : ''}{label}</span>
          ))}
        </div>
      )
    },
    { icon: <Icon.Chart />, color: 'var(--green)', title: 'Saiba o que você vendeu, de verdade', body: 'Relatório completo filtrado por período, com exportação em CSV pra sua contabilidade.' },
    { icon: <Icon.Box />, color: 'var(--blue)', title: 'Gerencie produtos sem enrolação', body: 'Catálogo com estoque, preço, SKU e status, com busca instantânea entre todos os seus anúncios.' },
    { icon: <Icon.Doc />, color: 'var(--navy)', title: 'Seus documentos fiscais em um clique', body: 'XML e PDF organizados por período, sem precisar caçar no ML.' },
    { icon: <Icon.Shield />, color: 'var(--navy)', title: 'Seus dados 100% seguros', body: 'Conexão via API oficial do Mercado Livre, criptografia de ponta a ponta, zero acesso a senha ou dados bancários.' },
    { icon: <Icon.Device />, color: 'var(--orange)', title: 'Trabalhe de qualquer lugar', body: '100% web, sem instalar nada, funciona no celular, tablet ou computador.' },
    { icon: <Icon.Moon />, color: 'var(--navy-deep)', title: 'Pare de ficar no celular à noite', body: 'Com o painel sempre atualizado, você fecha o dia sabendo que está tudo em ordem.' },
  ];

  return (
    <section className="lp-section" style={{ background: 'var(--bg-soft)' }}>
      <div className="lp-container">
        <Reveal>
          <div className="lp-eyebrow">— Benefícios</div>
          <h2 className="lp-display" style={{ fontSize: 'clamp(32px, 4.5vw, 64px)', fontWeight: 700, lineHeight: 0.98, letterSpacing: '-0.035em', marginBottom: 48, maxWidth: 800 }}>
            Tudo que você precisa.<br /><em style={{ fontStyle: 'italic', color: 'var(--ink-3)' }}>Nada que você não precisa.</em>
          </h2>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 18 }}>
          {benefits.map((b, i) => (
            <Reveal key={i} delay={i * 40}>
              <div className="lp-card" style={{ height: '100%' }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: b.color, color: b.color === 'var(--yellow-warm)' ? 'var(--navy-ink)' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>{b.icon}</div>
                <h3 className="lp-display" style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.015em', marginBottom: 8, color: 'var(--ink)' }}>{b.title}</h3>
                <p className="lp-card-body" style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)' }}>{b.body}</p>
                {b.extra}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Depoimentos ──────────────────────────────────────────────────────────
function SocialProof() {
  const testimonials = [
    { name: 'Rafael Siqueira', store: 'Acessórios Apple · SP', avatar: 'RS', color: 'var(--navy)', focus: 'Pedido que não atrasou', quote: 'Na terça passada entrou um FLEX 16:40. Meu celular apitou, postei às 17:52. Antes do Integra eu tinha certeza que perderia esse — e multa de FLEX atrasado não é pouca coisa.' },
    { name: 'Camila Prado', store: 'Papelaria criativa · Curitiba', avatar: 'CP', color: 'var(--red)', focus: 'Controle e tranquilidade', quote: 'Não abro mais o app do ML umas 20 vezes por dia. Deixo o Integra aberto numa aba e volto a viver. Primeiro fim de semana em meses que não fiquei ansiosa.' },
    { name: 'Diego Almeida', store: 'Pet shop online · BH · 300+ vendas/mês', avatar: 'DA', color: 'var(--blue)', focus: 'Organização financeira', quote: 'Exportei o CSV do mês em 2 cliques e mandei pro contador. Antes era planilha de madrugada. Sério, só por isso já vale.' },
  ];
  return (
    <section className="lp-section">
      <div className="lp-container">
        <Reveal>
          <div className="lp-eyebrow">— Quem já está usando</div>
          <h2 className="lp-display" style={{ fontSize: 'clamp(28px, 4vw, 54px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-0.03em', marginBottom: 48 }}>
            Primeiros usuários. <em style={{ fontStyle: 'italic', color: 'var(--ink-3)' }}>Resultados reais.</em>
          </h2>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }} className="lp-grid-3">
          {testimonials.map((t, i) => (
            <Reveal key={i} delay={i * 100}>
              <figure style={{ background: 'white', padding: 26, borderRadius: 14, border: '1px solid var(--line)', height: '100%', display: 'flex', flexDirection: 'column', margin: 0 }}>
                <div className="lp-mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.05em', marginBottom: 16, textTransform: 'uppercase' }}>↗ {t.focus}</div>
                <blockquote style={{ flex: 1, fontSize: 15, lineHeight: 1.6, color: 'var(--ink)', marginBottom: 20, fontWeight: 450 }}>"{t.quote}"</blockquote>
                <figcaption style={{ display: 'flex', alignItems: 'center', gap: 11, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: t.color, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Archivo', sans-serif", fontWeight: 700, fontSize: 12 }}>{t.avatar}</div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{t.store}</div>
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Oferta / Preço ───────────────────────────────────────────────────────
function Offer({ vagas, onRegister }) {
  const hasEarly = vagas === null || vagas > 0;
  const included = [
    'Dashboard em tempo real com dados ao vivo da sua loja',
    'Alertas sonoros para novos pedidos e perguntas',
    'Controle de prazo de envio FLEX, AGÊNCIA, RETIRADA e FULL',
    'Gestão completa de produtos (estoque, preço, SKU, status)',
    'Relatório de vendas com exportação em CSV',
    'Acesso a documentos fiscais (XML e PDF) por período',
    'Billing automático via PIX',
    'Dados criptografados · Conexão segura via API oficial do ML',
    'Atualizações futuras sem custo extra',
    'Suporte por WhatsApp',
  ];
  return (
    <section className="lp-section" id="preco" style={{ background: 'var(--navy-ink)', color: 'var(--bg)' }}>
      <div className="lp-container">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'start' }} className="lp-grid-2">
          <Reveal>
            <div className="lp-eyebrow" style={{ color: 'var(--yellow)' }}>— O que está incluso</div>
            <h2 className="lp-display" style={{ fontSize: 'clamp(28px, 4vw, 54px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-0.03em', marginBottom: 36 }}>
              Tudo. Por menos de <span style={{ color: 'var(--yellow)' }}>R$2</span> por dia.
            </h2>
            <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gap: 13 }}>
              {included.map((item, i) => (
                <li key={i} style={{ display: 'flex', gap: 13, alignItems: 'flex-start', fontSize: 15, color: 'rgba(250,248,243,0.88)' }}>
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--yellow)', color: 'var(--navy-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}><Icon.Check /></span>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={100}>
            <div style={{ background: 'var(--bg)', color: 'var(--ink)', borderRadius: 20, padding: 32, position: 'sticky', top: 96, boxShadow: '0 40px 80px -30px rgba(0,0,0,0.4)' }}>
              <div style={{ display: 'inline-block', background: 'var(--yellow)', color: 'var(--navy-ink)', borderRadius: 999, padding: '4px 13px', fontSize: 12, fontWeight: 700, marginBottom: 22 }}>
                🔥 Membro Fundador · 100 primeiros
              </div>
              {hasEarly && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span className="lp-display" style={{ fontSize: 18, color: 'var(--ink-3)', textDecoration: 'line-through', fontWeight: 500 }}>R$ {PRICE_FULL.toFixed(2).replace('.', ',')}</span>
                  <span className="lp-mono" style={{ fontSize: 10, background: 'var(--red)', color: 'white', padding: '2px 7px', borderRadius: 4, fontWeight: 700 }}>-30% VITALÍCIO</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 22 }}>
                <span className="lp-display" style={{ fontSize: 68, fontWeight: 900, color: 'var(--navy)', letterSpacing: '-0.04em', lineHeight: 1 }}>
                  R$&nbsp;{hasEarly ? Math.floor(PRICE_EARLY) : Math.floor(PRICE_FULL)}
                </span>
                <span className="lp-display" style={{ fontSize: 32, fontWeight: 800, color: 'var(--navy)' }}>
                  ,{hasEarly ? String(Math.round((PRICE_EARLY % 1) * 100)).padStart(2, '0') : String(Math.round((PRICE_FULL % 1) * 100)).padStart(2, '0')}
                </span>
                <span style={{ fontSize: 14, color: 'var(--ink-3)', marginLeft: 5 }}>/mês</span>
              </div>

              <div style={{ background: 'var(--bg-soft)', padding: '13px 16px', borderRadius: 10, marginBottom: 22, display: 'flex', alignItems: 'center', gap: 9 }}>
                <Icon.Spark />
                <span style={{ fontSize: 14, fontWeight: 500 }}><b>7 dias grátis</b> · Sem cartão · Cancele quando quiser</span>
              </div>

              <button className="lp-btn-yellow" style={{ width: '100%', justifyContent: 'center', padding: 18, fontSize: 16 }} onClick={onRegister}>
                Começar meus 7 dias grátis <Icon.ArrowRight />
              </button>
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 16, textAlign: 'center' }}>
                Após o período gratuito: <b style={{ color: 'var(--ink)' }}>R$ {hasEarly ? PRICE_EARLY.toFixed(2).replace('.', ',') : PRICE_FULL.toFixed(2).replace('.', ',')}/mês</b> · Pagamento via PIX
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

// ─── Objeções ─────────────────────────────────────────────────────────────
function Objections() {
  const qs = [
    { q: '"Mas o Mercado Livre já tem um painel..."', a: 'Tem. E ele te dá dados. O Integra MKP te dá controle. São coisas diferentes. O painel do ML foi feito pra atender milhões de categorias — não foi feito pra você, vendedor, que precisa de velocidade e clareza no dia a dia.' },
    { q: '"Será que funciona pra minha loja?"', a: 'Se você vende no Mercado Livre e quer ter mais controle, funciona. Não importa se você tem 10 anúncios ou 500.' },
    { q: '"Vou ter que aprender mais uma ferramenta..."', a: 'Não existe curva de aprendizado. Você conecta sua conta do ML e já começa a usar. Sem configuração, sem manual, sem vídeo de onboarding.' },
    { q: '"Meus dados ficam seguros?"', a: 'Sim. A conexão é feita via API oficial do Mercado Livre, com criptografia de ponta a ponta. O Integra MKP não acessa sua senha, seus dados bancários ou qualquer informação além do necessário pra exibir o painel.' },
    { q: `"R$${PRICE_FULL.toFixed(2).replace('.', ',')} é gasto a mais no mês..."`, a: 'Um pedido FLEX atrasado te custa multa + reputação. O painel paga a si mesmo na primeira vez que te evita um problema. E com 7 dias grátis, você testa sem gastar nada.' },
  ];
  return (
    <section className="lp-section">
      <div className="lp-container-narrow">
        <Reveal>
          <div className="lp-eyebrow">— Objeções comuns</div>
          <h2 className="lp-display" style={{ fontSize: 'clamp(28px, 4vw, 50px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-0.03em', marginBottom: 44 }}>
            Pensou nisso? <em style={{ fontStyle: 'italic', color: 'var(--ink-3)' }}>Pensamos também.</em>
          </h2>
        </Reveal>
        <div>
          {qs.map((item, i) => (
            <Reveal key={i} delay={i * 50}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 36, padding: '26px 0', borderTop: i === 0 ? '1px solid var(--line)' : 'none', borderBottom: '1px solid var(--line)' }} className="lp-grid-2">
                <div className="lp-display" style={{ fontSize: 19, fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.015em', lineHeight: 1.3 }}>{item.q}</div>
                <div style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ink-2)' }}>{item.a}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Garantia ─────────────────────────────────────────────────────────────
function Guarantee() {
  return (
    <section className="lp-section-sm" style={{ background: 'var(--bg-soft)' }}>
      <div className="lp-container-narrow">
        <Reveal>
          <div style={{ background: 'white', borderRadius: 18, padding: '44px 44px', border: '1px solid var(--line)', display: 'grid', gridTemplateColumns: '76px 1fr', gap: 28, alignItems: 'center' }} className="lp-grid-2">
            <div style={{ width: 76, height: 76, borderRadius: '50%', background: 'var(--yellow)', color: 'var(--navy-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon.Shield width={34} height={34} strokeWidth={2.2} />
            </div>
            <div>
              <div className="lp-eyebrow" style={{ marginBottom: 10 }}>— Nossa garantia</div>
              <h3 className="lp-display" style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 10 }}>Comece grátis. Sem cartão de crédito. 7 dias pra testar tudo sem pagar nada.</h3>
              <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ink-2)', marginBottom: 8 }}>Se depois dos 7 dias você não quiser continuar, é só não assinar. Sem cobrança, sem burocracia, sem precisar ligar pra ninguém.</p>
              <p style={{ fontSize: 15, color: 'var(--ink)', fontWeight: 500 }}>A confiança é nossa — não sua. Você descobre na prática antes de decidir.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Contador de vagas ────────────────────────────────────────────────────
function Scarcity({ vagas, onRegister }) {
  const taken = vagas === null ? EARLY_BASE : EARLY_TOTAL - vagas;
  const remaining = vagas === null ? EARLY_TOTAL - EARLY_BASE : vagas;
  const pct = (taken / EARLY_TOTAL) * 100;

  return (
    <section className="lp-section" style={{
      background: 'var(--yellow)',
      backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 24px, rgba(27,42,122,0.04) 24px 25px)',
      color: 'var(--navy-ink)',
    }}>
      <div className="lp-container-narrow">
        <Reveal>
          <div className="lp-mono" style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--navy)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--red)', animation: 'lp-pulse 1.5s ease-in-out infinite' }} />
            Vagas limitadas
          </div>
          <h2 className="lp-display" style={{ fontSize: 'clamp(32px, 4.5vw, 68px)', fontWeight: 800, lineHeight: 0.98, letterSpacing: '-0.035em', marginBottom: 20, maxWidth: 760 }}>
            🔥 Seja um dos 100 primeiros.<br />
            <span style={{ color: 'var(--red)' }}>30% de desconto vitalício.</span>
          </h2>
          <p style={{ fontSize: 17, lineHeight: 1.55, maxWidth: 680, marginBottom: 36 }}>
            Os primeiros 100 usuários entram com <b>30% de desconto permanente</b> — independente de qualquer reajuste futuro no preço. Esse desconto não expira, não some e não é renegociado.
          </p>

          <div style={{ background: 'var(--navy-ink)', color: 'var(--bg)', borderRadius: 16, padding: 28, boxShadow: '0 20px 50px -20px rgba(10,15,50,0.4)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div className="lp-mono" style={{ fontSize: 10, color: 'var(--yellow)', letterSpacing: '0.1em', marginBottom: 4 }}>VAGAS SÓCIO FUNDADOR</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                  <span className="lp-display" style={{ fontSize: 72, fontWeight: 900, lineHeight: 1, letterSpacing: '-0.04em' }}>{remaining}</span>
                  <span style={{ fontSize: 20, color: 'rgba(250,248,243,0.6)' }}>/ {EARLY_TOTAL} restantes</span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="lp-mono" style={{ fontSize: 10, color: 'rgba(250,248,243,0.5)', letterSpacing: '0.1em', marginBottom: 4 }}>PREENCHIMENTO</div>
                <div className="lp-display" style={{ fontSize: 38, fontWeight: 800, color: 'var(--yellow)' }}>{Math.round(pct)}%</div>
              </div>
            </div>
            <div style={{ height: 11, background: 'rgba(255,255,255,0.1)', borderRadius: 100, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, var(--yellow), var(--red))', transition: 'width 0.8s ease', animation: 'lp-progress-glow 2s ease-in-out infinite' }} />
            </div>
            <div style={{ marginTop: 20, fontSize: 13, color: 'rgba(250,248,243,0.7)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <span>Quando as 100 vagas fecharem, novos usuários entram pelo preço cheio.</span>
              <button onClick={onRegister} style={{ color: 'var(--yellow)', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 4, fontSize: 13 }}>
                Garantir minha vaga →
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Vantagens early adopter ──────────────────────────────────────────────
function EarlyPerks() {
  const perks = [
    { icon: '🗣️', title: 'Voz ativa no desenvolvimento', body: 'Suas sugestões vão direto pra fila de prioridade. Não é caixa de sugestão decorativa.' },
    { icon: '🔵', title: 'Acesso antecipado a novas funcionalidades', body: 'Você testa antes de todo mundo.' },
    { icon: '🤝', title: 'Canal direto com o time', body: 'Suporte por WhatsApp com quem construiu o sistema, não com atendente de script.' },
    { icon: '📊', title: 'Influência real no roadmap', body: 'O que os primeiros usuários pedem é o que entra no produto primeiro.' },
  ];
  return (
    <section className="lp-section">
      <div className="lp-container">
        <Reveal>
          <div style={{ maxWidth: 740, marginBottom: 48 }}>
            <div className="lp-eyebrow">— Vantagens exclusivas</div>
            <h2 className="lp-display" style={{ fontSize: 'clamp(28px, 4vw, 54px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-0.03em', marginBottom: 16 }}>
              Entrar agora não é só sobre preço. <em style={{ fontStyle: 'italic', color: 'var(--ink-3)' }}>É sobre posição.</em>
            </h2>
            <p style={{ fontSize: 17, color: 'var(--ink-2)', lineHeight: 1.6 }}>Os 100 primeiros usuários do Integra MKP fazem parte de algo que a maioria vai querer ter feito antes: ajudar a construir o produto.</p>
          </div>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0, border: '1px solid var(--line)', borderRadius: 16, overflow: 'hidden' }} className="lp-grid-3" data-cols="4">
          {perks.map((p, i) => (
            <Reveal key={i} delay={i * 80}>
              <div style={{ padding: '32px 24px', borderRight: i < perks.length - 1 ? '1px solid var(--line)' : 'none', height: '100%', background: 'white' }}>
                <div style={{ fontSize: 30, marginBottom: 16 }}>{p.icon}</div>
                <h3 className="lp-display" style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.015em', marginBottom: 9 }}>{p.title}</h3>
                <p style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)' }}>{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Roadmap ──────────────────────────────────────────────────────────────
function Roadmap() {
  const columns = [
    { status: 'ENTREGUE', date: 'Abril 2026', color: 'var(--green)', icon: '✅', items: ['Dashboard em tempo real', 'Alertas sonoros (pedidos + perguntas)', 'Controle de prazo FLEX, AGÊNCIA e RETIRADA', 'Gestão de produtos (estoque, preço, SKU, status)', 'Relatório de vendas + exportação CSV', 'Documentos fiscais (XML e PDF)', 'Billing automático via PIX', 'Criptografia de ponta a ponta'] },
    { status: 'EM DESENVOLVIMENTO', date: 'Maio 2026', color: 'var(--blue)', icon: '🔵', items: ['Modal de produto completo com edição direta', 'Calculadora de lucro real por produto', 'Lucro estimado exibido na tabela de produtos', 'Edição de anúncios direto na plataforma'] },
    { status: 'CHEGANDO', date: 'Junho 2026', color: 'var(--orange)', icon: '🚀', items: ['Relatório de performance por produto (12 meses)', 'Comparativo mensal com variação percentual', 'Relatório de devoluções', 'Alerta de queda de reputação'] },
  ];
  return (
    <section className="lp-section" id="roadmap" style={{ background: 'var(--bg-soft)' }}>
      <div className="lp-container">
        <Reveal>
          <div className="lp-eyebrow">— Roadmap</div>
          <h2 className="lp-display" style={{ fontSize: 'clamp(28px, 4vw, 54px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-0.03em', marginBottom: 20 }}>
            O que já entregamos. <em style={{ fontStyle: 'italic', color: 'var(--ink-3)' }}>O que vem por aí.</em>
          </h2>
          <p style={{ fontSize: 16, color: 'var(--ink-2)', maxWidth: 560, marginBottom: 44, lineHeight: 1.55 }}>Os early adopters participam ativamente da definição dessas prioridades.</p>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }} className="lp-grid-3">
          {columns.map((col, ci) => (
            <Reveal key={ci} delay={ci * 100}>
              <div style={{ background: 'white', borderRadius: 16, padding: 26, borderTop: `4px solid ${col.color}`, boxShadow: '0 2px 0 var(--line)', height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 5 }}>
                  <span style={{ fontSize: 16 }}>{col.icon}</span>
                  <span className="lp-mono" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: col.color }}>{col.status}</span>
                </div>
                <div className="lp-display" style={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 18 }}>{col.date}</div>
                <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {col.items.map((it, ii) => (
                    <li key={ii} style={{ display: 'flex', gap: 9, fontSize: 13, lineHeight: 1.45, color: 'var(--ink-2)' }}>
                      <span style={{ color: col.color, fontWeight: 700, flexShrink: 0 }}>—</span>{it}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(0);
  const items = [
    { q: 'Preciso instalar algum programa?', a: 'Não. O Integra MKP é 100% web. Funciona no navegador, no celular, no tablet — em qualquer dispositivo, sem instalar nada.' },
    { q: 'Como começo os 7 dias grátis?', a: 'Cria sua conta, conecta sua loja do Mercado Livre e já começa a usar. Sem cartão de crédito. O painel já está funcionando em menos de 2 minutos.' },
    { q: 'Meus dados ficam seguros?', a: 'Sim. Conexão via API oficial do Mercado Livre, criptografia de ponta a ponta. O sistema não acessa sua senha nem seus dados bancários.' },
    { q: 'Funciona pra qualquer tipo de loja no ML?', a: 'Sim. Seja você vendedor de eletrônicos, moda, pets ou qualquer outra categoria.' },
    { q: 'Como funciona o pagamento após os 7 dias?', a: 'Mensal via PIX, gerado automaticamente no painel. Sem cartão de crédito obrigatório.' },
    { q: 'Tem suporte? Como funciona?', a: 'Sim, por WhatsApp. Você fala com quem construiu o produto.' },
    { q: 'Se eu cancelar, perco o desconto de early adopter?', a: 'Sim. O desconto de 30% é garantido enquanto a assinatura está ativa. Se cancelar e voltar, o preço será o vigente na época.' },
  ];
  return (
    <section className="lp-section" id="faq">
      <div className="lp-container-narrow">
        <Reveal>
          <div className="lp-eyebrow">— Perguntas frequentes</div>
          <h2 className="lp-display" style={{ fontSize: 'clamp(28px, 4vw, 54px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-0.03em', marginBottom: 44 }}>FAQ</h2>
        </Reveal>
        {items.map((it, i) => (
          <div key={i} style={{ borderTop: '1px solid var(--line)', borderBottom: i === items.length - 1 ? '1px solid var(--line)' : 'none' }}>
            <button onClick={() => setOpen(open === i ? -1 : i)} style={{ width: '100%', padding: '22px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left', cursor: 'pointer', background: 'none', border: 'none', fontFamily: "'Archivo', sans-serif", fontSize: 18, fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.01em' }}>
              <span>{it.q}</span>
              <span style={{ color: 'var(--navy)', transition: 'transform 0.2s ease', transform: open === i ? 'rotate(45deg)' : 'rotate(0)', flexShrink: 0, marginLeft: 16 }}><Icon.Plus /></span>
            </button>
            <div className="lp-faq-answer" style={{ maxHeight: open === i ? 300 : 0 }}>
              <p style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.6, paddingBottom: 22, maxWidth: 680 }}>{it.a}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Blog ─────────────────────────────────────────────────────────────────
const DOR_BLOG = {
  'pedidos-atrasados':    { label: 'Alertas',    color: '#d97706', bg: '#fffbeb' },
  'dashboard-tempo-real': { label: 'Dashboard',  color: '#0369a1', bg: '#f0f9ff' },
  'flex-cores':           { label: 'FLEX',        color: '#16a34a', bg: '#f0fdf4' },
  'documentos-fiscais':   { label: 'Documentos', color: '#7c3aed', bg: '#f5f3ff' },
  'perguntas-clientes':   { label: 'Perguntas',  color: '#db2777', bg: '#fdf2f8' },
  'fechar-mes':           { label: 'Relatórios', color: '#1B2A7A', bg: '#EEF2FF' },
};

function BlogSection() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetch(`${WORKER_URL}/api/blog?page=1`)
      .then(r => r.json())
      .then(d => setPosts((d.posts || []).slice(0, 3)))
      .catch(() => {});
  }, []);

  if (!posts.length) return null;

  return (
    <section className="lp-section" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)' }}>
      <div className="lp-container">
        <Reveal>
          <div className="lp-eyebrow">— Blog</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 44, flexWrap: 'wrap', gap: 12 }}>
            <h2 className="lp-display" style={{ fontSize: 'clamp(26px, 3.5vw, 44px)', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.03em' }}>
              O que todo vendedor do<br />
              <span style={{ color: 'var(--navy)' }}>Mercado Livre</span> precisa saber.
            </h2>
            <a href="/blog" style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)', textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Ver todos os artigos →
            </a>
          </div>
        </Reveal>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {posts.map((p, i) => {
            const dor = DOR_BLOG[p.dor_tema];
            return (
              <Reveal key={p.slug} delay={i * 80}>
                <a href={`/blog/${p.slug}`} style={{ display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 14, border: '1px solid var(--line)', padding: 24, textDecoration: 'none', height: '100%', transition: 'box-shadow 0.18s, transform 0.18s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.09)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}>
                  {dor && (
                    <span style={{ display: 'inline-block', alignSelf: 'flex-start', fontSize: 11, fontWeight: 700, color: dor.color, background: dor.bg, padding: '3px 9px', borderRadius: 20, marginBottom: 12 }}>
                      {dor.label}
                    </span>
                  )}
                  <h3 style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 800, fontSize: 17, color: 'var(--ink)', lineHeight: 1.3, letterSpacing: '-0.01em', marginBottom: 10, flex: 1 }}>
                    {p.title}
                  </h3>
                  {p.summary && (
                    <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55, marginBottom: 16, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {p.summary}
                    </p>
                  )}
                  <span style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 'auto' }}>
                    {new Date(p.published_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </a>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── CTA Final ────────────────────────────────────────────────────────────
function FinalCTA({ onRegister }) {
  return (
    <section id="cta" style={{ background: 'var(--navy-ink)', color: 'var(--bg)', padding: '96px 0', position: 'relative', overflow: 'hidden' }}>
      <div aria-hidden style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,212,0,0.15), transparent 50%), radial-gradient(circle at 80% 70%, rgba(46,125,224,0.15), transparent 50%)' }} />
      <div className="lp-container-narrow" style={{ position: 'relative', textAlign: 'center' }}>
        <Reveal>
          <div className="lp-eyebrow" style={{ color: 'var(--yellow)', justifyContent: 'center', display: 'flex', marginBottom: 20 }}>— Último call</div>
          <h2 className="lp-display" style={{ fontSize: 'clamp(36px, 5.5vw, 80px)', fontWeight: 800, lineHeight: 0.98, letterSpacing: '-0.035em', marginBottom: 28 }}>
            Você já sabe o que é <span style={{ color: 'var(--yellow)' }}>não ter controle.</span>
          </h2>
          <p style={{ fontSize: 18, lineHeight: 1.55, maxWidth: 600, margin: '0 auto 14px', color: 'rgba(250,248,243,0.8)' }}>
            Pedido que apareceu tarde. Prazo que quase venceu. Mês que fechou sem você saber direito o que vendeu.
          </p>
          <p className="lp-display" style={{ fontSize: 20, fontWeight: 600, marginBottom: 40, maxWidth: 600, margin: '0 auto 40px' }}>
            Isso tem solução. E você pode testar gratuitamente agora.
          </p>
          <p style={{ fontSize: 14, color: 'rgba(250,248,243,0.6)', marginBottom: 28 }}>
            7 dias grátis, sem cartão, sem compromisso. Se gostar, continua por menos de R$2 por dia. Se não gostar, não paga nada.
          </p>
          <button className="lp-btn-yellow" style={{ padding: '20px 36px', fontSize: 18 }} onClick={onRegister}>
            Começar meus 7 dias grátis <Icon.ArrowRight />
          </button>
          <div className="lp-microcopy" style={{ justifyContent: 'center', marginTop: 18, color: 'rgba(250,248,243,0.6)' }}>
            <span>Sem cartão de crédito</span>
            <span>Acesso imediato</span>
            <span>Cancele quando quiser</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── PS ───────────────────────────────────────────────────────────────────
function PS() {
  return (
    <section style={{ background: 'var(--navy-ink)', color: 'rgba(250,248,243,0.7)', padding: '52px 0', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="lp-container-narrow">
        <Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: '56px 1fr', gap: 22, alignItems: 'flex-start' }}>
            <div className="lp-display" style={{ fontSize: 42, color: 'var(--yellow)', fontWeight: 900, lineHeight: 1 }}>PS.</div>
            <p style={{ fontSize: 15, lineHeight: 1.6 }}>
              Os 100 primeiros usuários garantem <b style={{ color: 'var(--yellow)' }}>30% de desconto permanente</b> — independente de qualquer reajuste de preço no futuro. Não é promoção. É a sua posição como early adopter, para sempre. Quando as vagas acabarem, esse desconto fecha junto.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer style={{ background: 'var(--navy-ink)', color: 'rgba(250,248,243,0.5)', padding: '44px 0 36px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="lp-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 20 }}>
        <div>
          <IntegraLogo size="sm" />
          <div className="lp-mono" style={{ fontSize: 11, marginTop: 12 }}>© 2026 Integra MKP · Feito por quem vende, para quem vende.</div>
        </div>
        <div className="lp-mono" style={{ fontSize: 11, display: 'flex', gap: 22 }}>
          <a href="#" style={{ color: 'rgba(250,248,243,0.5)', textDecoration: 'none' }}>Termos</a>
          <a href="#" style={{ color: 'rgba(250,248,243,0.5)', textDecoration: 'none' }}>Privacidade</a>
          <a href="mailto:contato@integramkp.com.br" style={{ color: 'rgba(250,248,243,0.5)', textDecoration: 'none' }}>Suporte</a>
        </div>
      </div>
    </footer>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────
export default function LandingPage() {
  const [showLogin, setShowLogin] = useState(false);
  const [loginMode, setLoginMode] = useState('login');
  const [vagas, setVagas] = useState(null);
  const [referrerName, setReferrerName] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('referral_code', ref.toLowerCase());
      fetch(`${WORKER_URL}/api/referral/lookup?code=${encodeURIComponent(ref)}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.name) setReferrerName(d.name); })
        .catch(() => {});
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.getElementById('root').style.overflow = 'auto';
    document.getElementById('root').style.height = 'auto';
    return () => {
      document.body.style.overflow = '';
      document.getElementById('root').style.overflow = '';
      document.getElementById('root').style.height = '';
    };
  }, []);

  useEffect(() => {
    fetch(`${WORKER_URL}/api/early-count`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d !== null) {
          const count = d.count || 0;
          const remaining = Math.max(0, EARLY_BASE - Math.floor(count / 2));
          setVagas(remaining);
        }
      })
      .catch(() => setVagas(EARLY_TOTAL - EARLY_BASE));
  }, []);

  const openLogin = (mode) => { setLoginMode(mode); setShowLogin(true); };

  return (
    <>
      <style>{CSS_VARS}</style>

      {/* Banner de indicação */}
      {referrerName && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 3000, backgroundColor: '#16a34a', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '16px' }}>🎁</span>
          <span style={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
            <strong>{referrerName}</strong> te indicou — cadastre-se e ganhe <strong>15% off</strong> na primeira mensalidade
          </span>
          <button onClick={() => openLogin('register')} style={{ backgroundColor: 'white', color: '#16a34a', border: 'none', borderRadius: '6px', padding: '5px 14px', fontWeight: 700, cursor: 'pointer', fontSize: '0.82rem' }}>
            Cadastrar agora
          </button>
        </div>
      )}

      {/* Modal de login/cadastro */}
      {showLogin && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setShowLogin(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
        >
          <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 620, position: 'relative', boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }}>
            <button onClick={() => setShowLogin(false)} style={{ position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#999', zIndex: 10, lineHeight: 1 }}>✕</button>
            <Login initialMode={loginMode} />
          </div>
        </div>
      )}

      {referrerName && <div style={{ height: '44px' }} />}
      <Nav onLogin={() => openLogin('login')} onRegister={() => openLogin('register')} />
      <Hero onRegister={() => openLogin('register')} />
      <Opening />
      <Pain />
      <Solution />
      <Benefits />
      <SocialProof />
      <Offer vagas={vagas} onRegister={() => openLogin('register')} />
      <Objections />
      <Guarantee />
      <Scarcity vagas={vagas} onRegister={() => openLogin('register')} />
      <EarlyPerks />
      <Roadmap />
      <FAQ />
      <BlogSection />
      <FinalCTA onRegister={() => openLogin('register')} />
      <PS />
      <Footer />
    </>
  );
}
